'use client';

import { type RefObject, useCallback } from 'react';
import { PUSHER_CHANNEL_PREFIX } from '../../consts/pusher/channelType';
import { PUSHER_METHOD_TYPES, type PusherMethod } from '../../consts/pusher/methods';
import type { PusherChannel } from '../../consts/pusher/sdk';

interface UsePusherMethodsParams {
  initializePusher: (authValue: string) => Promise<boolean>;
  subscribeToChannel: (channel: string) => void;
  unsubscribeFromChannel: (channel: string) => void;
  channelsRef: RefObject<Map<string, PusherChannel>>;
  resetChannel: () => void;
}

export function usePusherMethods({
  initializePusher,
  subscribeToChannel,
  unsubscribeFromChannel,
  channelsRef,
  resetChannel,
}: UsePusherMethodsParams) {
  const handlePusherMethod = useCallback(
    async (event: PusherMethod) => {
      switch (event.eventType) {
        case PUSHER_METHOD_TYPES.init: {
          const previousChannels = [...channelsRef.current.keys()].map((targetChannelName) =>
            targetChannelName.replace(PUSHER_CHANNEL_PREFIX, ''),
          );
          const didReconnect = await initializePusher(event.eventPayload.authValue);
          if (didReconnect && previousChannels.length) {
            resetChannel();
            for (const channel of previousChannels) {
              subscribeToChannel(channel);
            }
          }
          return;
        }
        case PUSHER_METHOD_TYPES.subscribe:
          subscribeToChannel(event.eventPayload.channel);
          return;
        case PUSHER_METHOD_TYPES.unsubscribe:
          unsubscribeFromChannel(event.eventPayload.channel);
          return;
        default:
          return;
      }
    },
    [initializePusher, subscribeToChannel, unsubscribeFromChannel, channelsRef, resetChannel],
  );

  return { handlePusherMethod } as const;
}
